import React, { useState } from "react";

import {

  Box,

  Typography,

  Checkbox,

  TextField,

  IconButton,

  LinearProgress,

  Stack,

  alpha,

  useTheme,

  CircularProgress,

} from "@mui/material";

import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useTranslation } from "react-i18next";

import { useTeamBoardAction } from "../hooks/useTeamBoardAction";



const TaskCardChecklists = ({ checklists = [], canManage, cardId, mutations }) => {

  const { t } = useTranslation("teamTasks");

  const theme = useTheme();

  const { run } = useTeamBoardAction();

  const [newItemText, setNewItemText] = useState({});

  const [addingTo, setAddingTo] = useState(null);

  const [busyItemId, setBusyItemId] = useState(null);

  const [addingChecklistId, setAddingChecklistId] = useState(null);

  const [deletingChecklistId, setDeletingChecklistId] = useState(null);



  const handleToggleItem = async (item) => {

    if (!canManage || !mutations?.updateChecklistItem || busyItemId) return;

    setBusyItemId(item.id);

    try {

      await run(

        () =>

          mutations.updateChecklistItem.mutateAsync({

            cardId,

            itemId: item.id,

            isDone: !item.isDone,

          }),

        { context: "checklist" }

      );

    } finally {

      setBusyItemId(null);

    }

  };



  const handleAddItem = async (checklistId) => {

    const title = String(newItemText[checklistId] || "").trim();

    if (!title || !mutations?.createChecklistItem || addingChecklistId) return;

    setAddingChecklistId(checklistId);

    try {

      const result = await run(

        () => mutations.createChecklistItem.mutateAsync({ cardId, checklistId, title }),

        { context: "checklist" }

      );

      if (result === null) return;

      setNewItemText((prev) => ({ ...prev, [checklistId]: "" }));

      setAddingTo(null);

    } finally {

      setAddingChecklistId(null);

    }

  };



  const handleDeleteItem = async (itemId) => {

    if (!mutations?.deleteChecklistItem || busyItemId) return;

    setBusyItemId(itemId);

    try {

      await run(

        () => mutations.deleteChecklistItem.mutateAsync({ cardId, itemId }),

        { context: "checklist" }

      );

    } finally {

      setBusyItemId(null);

    }

  };



  const handleDeleteChecklist = async (checklistId) => {

    if (!window.confirm(t("deleteChecklistConfirm"))) return;

    if (!mutations?.deleteChecklist || deletingChecklistId) return;

    setDeletingChecklistId(checklistId);

    try {

      await run(

        () => mutations.deleteChecklist.mutateAsync({ cardId, checklistId }),

        { context: "checklist" }

      );

    } finally {

      setDeletingChecklistId(null);

    }

  };



  if (!checklists.length) return null;



  return (

    <Box sx={{ mb: 2.5 }}>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>

        <CheckBoxOutlinedIcon fontSize="small" color="action" />

        <Typography variant="subtitle2" fontWeight={700}>

          {t("checklistsSection")}

        </Typography>

      </Stack>



      <Stack spacing={2}>

        {checklists.map((checklist) => {

          const items = checklist.items || [];

          const doneCount = items.filter((i) => i.isDone).length;

          const total = items.length;

          const progress = total > 0 ? Math.round((doneCount / total) * 100) : 0;

          const isAddingHere = addingChecklistId === checklist.id;



          return (

            <Box

              key={checklist.id}

              sx={{

                p: 1.5,

                borderRadius: 2,

                bgcolor: alpha(theme.palette.background.default, 0.6),

                border: `1px solid ${theme.palette.divider}`,

                opacity: deletingChecklistId === checklist.id ? 0.6 : 1,

              }}

            >

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>

                <Typography variant="body2" fontWeight={700}>

                  {checklist.title}

                </Typography>

                {canManage ? (

                  <IconButton

                    size="small"

                    onClick={() => handleDeleteChecklist(checklist.id)}

                    disabled={Boolean(deletingChecklistId)}

                    aria-label={t("deleteChecklist")}

                  >

                    {deletingChecklistId === checklist.id ? (

                      <CircularProgress size={16} />

                    ) : (

                      <DeleteOutlineIcon fontSize="small" />

                    )}

                  </IconButton>

                ) : null}

              </Stack>



              {total > 0 ? (

                <Box sx={{ mb: 1.25 }}>

                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>

                    {t("checklistProgress", { done: doneCount, total })}

                  </Typography>

                  <LinearProgress

                    variant="determinate"

                    value={progress}

                    sx={{ height: 6, borderRadius: 3 }}

                  />

                </Box>

              ) : null}



              <Stack spacing={0.25}>

                {items.map((item) => (

                  <Stack

                    key={item.id}

                    direction="row"

                    alignItems="flex-start"

                    spacing={0.5}

                    sx={{

                      py: 0.35,

                      borderRadius: 1,

                      "&:hover .checklist-item-delete": { opacity: 1 },

                    }}

                  >

                    <Checkbox

                      size="small"

                      checked={Boolean(item.isDone)}

                      disabled={!canManage || busyItemId === item.id}

                      onChange={() => handleToggleItem(item)}

                      sx={{ p: 0.5, mt: -0.25 }}

                    />

                    <Typography

                      variant="body2"

                      sx={{

                        flex: 1,

                        pt: 0.5,

                        textDecoration: item.isDone ? "line-through" : "none",

                        color: item.isDone ? "text.secondary" : "text.primary",

                        wordBreak: "break-word",

                      }}

                    >

                      {item.title}

                    </Typography>

                    {canManage ? (

                      <IconButton

                        className="checklist-item-delete"

                        size="small"

                        onClick={() => handleDeleteItem(item.id)}

                        disabled={Boolean(busyItemId)}

                        sx={{ opacity: 0, transition: "opacity 0.12s", mt: -0.25 }}

                        aria-label={t("deleteChecklistItem")}

                      >

                        {busyItemId === item.id ? (

                          <CircularProgress size={14} />

                        ) : (

                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />

                        )}

                      </IconButton>

                    ) : null}

                  </Stack>

                ))}

              </Stack>



              {canManage && (addingTo === checklist.id || items.length === 0) ? (

                <Box sx={{ mt: 1 }}>

                  <TextField

                    size="small"

                    fullWidth

                    placeholder={t("checklistItemPlaceholder")}

                    value={newItemText[checklist.id] || ""}

                    onChange={(e) =>

                      setNewItemText((prev) => ({ ...prev, [checklist.id]: e.target.value }))

                    }

                    onKeyDown={(e) => {

                      if (e.key === "Enter") {

                        e.preventDefault();

                        handleAddItem(checklist.id);

                      }

                      if (e.key === "Escape") setAddingTo(null);

                    }}

                    autoFocus={addingTo === checklist.id}

                    disabled={isAddingHere}

                  />

                  <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>

                    <Typography

                      component="button"

                      type="button"

                      onClick={() => handleAddItem(checklist.id)}

                      disabled={isAddingHere || !String(newItemText[checklist.id] || "").trim()}

                      sx={{

                        border: "none",

                        bgcolor: "primary.main",

                        color: "primary.contrastText",

                        px: 1.5,

                        py: 0.5,

                        borderRadius: 1,

                        fontSize: "0.8125rem",

                        fontWeight: 600,

                        cursor: isAddingHere ? "wait" : "pointer",

                        fontFamily: "inherit",

                        opacity: isAddingHere ? 0.75 : 1,

                        display: "inline-flex",

                        alignItems: "center",

                        gap: 0.75,

                      }}

                    >

                      {isAddingHere ? (

                        <>

                          <CircularProgress size={12} color="inherit" />

                          {t("addingCard")}

                        </>

                      ) : (

                        t("checklistAddItem")

                      )}

                    </Typography>

                    {items.length > 0 ? (

                      <Typography

                        component="button"

                        type="button"

                        onClick={() => setAddingTo(null)}

                        disabled={isAddingHere}

                        sx={{

                          border: "none",

                          bgcolor: "transparent",

                          color: "text.secondary",

                          px: 1,

                          py: 0.5,

                          fontSize: "0.8125rem",

                          cursor: "pointer",

                          fontFamily: "inherit",

                        }}

                      >

                        {t("cancel")}

                      </Typography>

                    ) : null}

                  </Stack>

                </Box>

              ) : canManage ? (

                <Typography

                  component="button"

                  type="button"

                  onClick={() => setAddingTo(checklist.id)}

                  disabled={Boolean(addingChecklistId || deletingChecklistId)}

                  sx={{

                    mt: 1,

                    border: "none",

                    bgcolor: "transparent",

                    color: "text.secondary",

                    fontSize: "0.8125rem",

                    cursor: "pointer",

                    fontFamily: "inherit",

                    "&:hover": { color: "primary.main" },

                    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },

                  }}

                >

                  {t("checklistAddItemAction")}

                </Typography>

              ) : null}

            </Box>

          );

        })}

      </Stack>

    </Box>

  );

};



export default TaskCardChecklists;

